/**
 * Base service class for API calls
 * Provides common functionality for all API services
 */

import * as Sentry from '@sentry/react'
import { API_CONFIG } from '@/config/api'
import { createApiUrlBuilder, type ApiUrlBuilder } from '@/utils/apiUrlBuilder'

export interface ApiResponse<T = any> {
  data: T
  status: number
  statusText: string
}

export interface PaginatedResponse<T = any> {
  count: number
  next: string | null
  previous: string | null
  results: T[]
}

/**
 * Coerce a DRF error value to a usable string. DRF wraps the string values of
 * a `ValidationError({'code': '...', 'detail': '...'})` in *arrays* of
 * ErrorDetail (e.g. `{"code": ["invalid_credentials"], "detail": ["Invalid login credentials."]}`),
 * so a plain `typeof x === 'string'` check misses them. This accepts a string,
 * or the first string in an array, and returns undefined for anything else.
 */
function firstString(value: unknown): string | undefined {
  if (typeof value === 'string') {
    return value
  }
  if (Array.isArray(value)) {
    const found = value.find((item) => typeof item === 'string')
    if (typeof found === 'string') {
      return found
    }
  }
  return undefined
}

function extractErrorCode(errorData: Record<string, unknown>): string | undefined {
  const direct = firstString(errorData.code)
  if (direct) {
    return direct
  }
  const detail = errorData.detail
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailObj = detail as Record<string, unknown>
    const nested = firstString(detailObj.code)
    if (nested) {
      return nested
    }
  }
  if (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length > 0) {
    const first = errorData.non_field_errors[0]
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const firstObj = first as Record<string, unknown>
      const code = firstString(firstObj.code)
      if (code) {
        return code
      }
    }
  }
  return undefined
}

function extractErrorMessage(errorData: Record<string, unknown>, status: number): string {
  const detail = errorData.detail
  // `detail` may be a string, an array of strings (DRF wraps ValidationError
  // dict values in arrays), or a nested object with its own `detail`.
  const detailStr = firstString(detail)
  if (detailStr) {
    return detailStr
  }
  if (detail && typeof detail === 'object' && !Array.isArray(detail)) {
    const detailObj = detail as Record<string, unknown>
    const nested = firstString(detailObj.detail)
    if (nested) {
      return nested
    }
  }
  if (typeof errorData.message === 'string') {
    return errorData.message
  }
  if (typeof errorData.error === 'string') {
    return errorData.error
  }
  if (Array.isArray(errorData.non_field_errors) && errorData.non_field_errors.length > 0) {
    const first = errorData.non_field_errors[0]
    if (typeof first === 'string') {
      return first
    }
    if (first && typeof first === 'object' && !Array.isArray(first)) {
      const firstObj = first as Record<string, unknown>
      if (typeof firstObj.detail === 'string') {
        return firstObj.detail
      }
    }
  }
  const fieldErrors: string[] = []
  for (const [field, messages] of Object.entries(errorData)) {
    if (field === 'detail' || field === 'message' || field === 'error' || field === 'non_field_errors' || field === 'code') {
      continue
    }
    if (Array.isArray(messages) && messages.length > 0) {
      fieldErrors.push(`${field}: ${String(messages[0])}`)
    } else if (typeof messages === 'string') {
      fieldErrors.push(`${field}: ${messages}`)
    }
  }
  if (fieldErrors.length > 0) {
    return fieldErrors.join(', ')
  }
  return `HTTP error! status: ${status}`
}

/**
 * Build an ApiError for a failed `fetch` (i.e. the request never produced an
 * HTTP response). This covers offline, DNS failure, connection refused, CORS
 * rejection and TLS errors. Browsers report these inconsistently — Chrome says
 * "Failed to fetch", Safari says "Load failed" — so we never surface the raw
 * message. Instead we attach a stable `code` that the UI can translate.
 */
export function buildNetworkError(err: unknown): ApiError {
  const offline = typeof navigator !== 'undefined' && navigator.onLine === false
  const code = offline ? 'offline' : 'network_error'
  // Human-readable fallback used only if the app hasn't loaded a translation
  // for this code yet.
  const message = offline
    ? 'You appear to be offline. Check your internet connection and try again.'
    : "Couldn't reach the server. Please check your connection and try again."
  const detail = err instanceof Error ? err.message : String(err)
  return new ApiError(message, 0, 'NetworkError', { detail }, code)
}

export class ApiError extends Error {
  status: number
  statusText: string
  code?: string
  data?: any

  constructor(message: string, status: number, statusText: string, data?: any, code?: string) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.statusText = statusText
    this.code = code
    this.data = data
  }
}

/**
 * Send genuinely-broken API calls to Sentry. We do NOT report expected 4xx
 * (validation, 401, 403, 404) — those are normal app flow and would just be
 * noise. We DO report:
 *   - 5xx           the backend blew up (this is the silent "brak danych" case)
 *   - status 0      network failure / CORS / DNS (request never got a response)
 *   - forceReport   parse failures (e.g. HTML returned where JSON was expected)
 *
 * Safe to call when Sentry isn't initialised (DSN unset) — captureException is
 * then a no-op. The `url` is reduced to its path so query strings / tokens in
 * the public respondent links don't leak into the issue title.
 */
function reportApiFailure(
  error: ApiError,
  endpoint: string,
  method: string,
  forceReport = false,
): void {
  const worthReporting = forceReport || error.status >= 500 || error.status === 0
  if (!worthReporting) return
  // Group explicitly so Sentry doesn't split one class of failure across dozens
  // of issues by stack/call-site. Network failures (status 0) all collapse into a
  // single issue per code (offline vs unreachable) — these are transient client
  // connectivity, not distinct bugs. A real backend 5xx stays separated per
  // endpoint+status so genuine server bugs remain individually triageable.
  const fingerprint =
    error.status === 0
      ? ['api-network-failure', error.code ?? 'network_error']
      : error.status >= 500
        ? ['api-5xx', method, endpoint, String(error.status)]
        : ['api-parse-failure', endpoint]
  Sentry.captureException(error, {
    fingerprint,
    tags: {
      api_endpoint: endpoint,
      http_method: method,
      http_status: String(error.status),
      api_error_code: error.code ?? 'none',
    },
    contexts: {
      api: {
        endpoint,
        method,
        status: error.status,
        statusText: error.statusText,
        code: error.code,
      },
    },
  })
}

export abstract class BaseService {
  protected baseUrl: string
  protected urlBuilder: ApiUrlBuilder

  constructor(baseUrl?: string) {
    this.baseUrl = baseUrl || API_CONFIG.BASE_URL
    this.urlBuilder = createApiUrlBuilder(this.baseUrl)
  }

  /**
   * Get authentication token from localStorage
   */
  protected getAuthToken(): string | null {
    return localStorage.getItem('access_token')
  }

  /**
   * Get refresh token from localStorage
   */
  protected getRefreshToken(): string | null {
    return localStorage.getItem('refresh_token')
  }

  /**
   * Check if token is expired
   */
  protected isTokenExpired(token: string): boolean {
    try {
      const payload = JSON.parse(atob(token.split('.')[1]))
      const currentTime = Date.now() / 1000
      return payload.exp < currentTime
    } catch {
      return true
    }
  }

  protected async parseOkResponseBody<T>(response: Response): Promise<T> {
    if (response.status === 204 || response.status === 205) {
      return undefined as T
    }
    const text = await response.text()
    const trimmed = text.trim()
    if (!trimmed) {
      return undefined as T
    }
    const contentType = response.headers.get('content-type') || ''
    try {
      return JSON.parse(trimmed) as T
    } catch {
      const hint =
        trimmed.startsWith('<') || contentType.includes('text/html')
          ? 'Received HTML instead of JSON (wrong API URL, SPA fallback, or proxy error). '
          : ''
      const preview = trimmed.length > 200 ? `${trimmed.slice(0, 200)}…` : trimmed
      const parseError = new ApiError(
        `${hint}Expected JSON from API (${response.url}). Content-Type: ${contentType || 'unknown'}. Body starts with: ${preview}`,
        response.status,
        response.statusText,
        { rawBodyStart: preview }
      )
      // A successful-looking response that isn't JSON is genuinely broken even
      // when the status is 200 — force-report it regardless of status.
      let path = response.url
      try { path = new URL(response.url).pathname } catch { /* keep raw */ }
      reportApiFailure(parseError, path, 'GET', true)
      throw parseError
    }
  }

  /**
   * Make authenticated API request
   */
  protected async request<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = this.urlBuilder.build(endpoint)
    const token = this.getAuthToken()

    const isFormData = options.body instanceof FormData
    const defaultHeaders: Record<string, string> = isFormData 
      ? {} 
      : { ...API_CONFIG.DEFAULT_HEADERS }

    const providedHeaders = options.headers as Record<string, string> | undefined
    const headers: Record<string, string> = {
      ...defaultHeaders,
      ...(token && { Authorization: `Bearer ${token}` }),
      ...providedHeaders,
    }

    const config: RequestInit = {
      ...options,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    }

    const method = (options.method ?? 'GET').toUpperCase()

    let response: Response
    try {
      response = await fetch(url, config)
    } catch (err) {
      const networkError = buildNetworkError(err)
      reportApiFailure(networkError, endpoint, method)
      throw networkError
    }

    if (!response.ok) {
      let errorData: any = {}
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        try {
          errorData = await response.json()
        } catch (e) {
          const text = await response.text().catch(() => '')
          errorData = { detail: text || `HTTP error! status: ${response.status}` }
        }
      } else {
        const text = await response.text().catch(() => '')
        errorData = { detail: text || `HTTP error! status: ${response.status}` }
      }

      const errorCode = extractErrorCode(errorData)
      const errorMessage = extractErrorMessage(errorData, response.status)

      // An authenticated request rejected with 401 means the session is no
      // longer valid (token missing, expired or revoked). Signal the app to
      // clear auth and redirect to login instead of leaving callers to swallow
      // the error and render a broken, authenticated-looking page.
      if (response.status === 401 && typeof window !== 'undefined') {
        window.dispatchEvent(new CustomEvent('auth:unauthorized'))
      }

      const apiError = new ApiError(
        errorMessage,
        response.status,
        response.statusText,
        errorData,
        errorCode,
      )
      reportApiFailure(apiError, endpoint, method)
      throw apiError
    }

    return this.parseOkResponseBody<T>(response)
  }

  /**
   * Make API request with explicit token (e.g. right after login before localStorage is read by other code paths).
   */
  protected async requestWithToken<T>(
    endpoint: string,
    token: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = this.urlBuilder.build(endpoint)
    const isFormData = options.body instanceof FormData
    const defaultHeaders: Record<string, string> = isFormData
      ? {}
      : { ...API_CONFIG.DEFAULT_HEADERS }
    const providedHeaders = options.headers as Record<string, string> | undefined
    const headers: Record<string, string> = {
      ...defaultHeaders,
      Authorization: `Bearer ${token}`,
      ...providedHeaders,
    }
    const config: RequestInit = {
      ...options,
      headers: Object.keys(headers).length > 0 ? headers : undefined,
    }
    const method = (options.method ?? 'GET').toUpperCase()

    let response: Response
    try {
      response = await fetch(url, config)
    } catch (err) {
      const networkError = buildNetworkError(err)
      reportApiFailure(networkError, endpoint, method)
      throw networkError
    }
    if (!response.ok) {
      const contentType = response.headers.get('content-type')
      let errorData: any = {}
      if (contentType && contentType.includes('application/json')) {
        try {
          errorData = await response.json()
        } catch {
          errorData = { detail: await response.text().catch(() => '') }
        }
      } else {
        errorData = { detail: await response.text().catch(() => '') }
      }
      const errorCode = extractErrorCode(errorData)
      const errorMessage = extractErrorMessage(errorData, response.status)
      const apiError = new ApiError(errorMessage, response.status, response.statusText, errorData, errorCode)
      reportApiFailure(apiError, endpoint, method)
      throw apiError
    }
    return this.parseOkResponseBody<T>(response)
  }

  /**
   * Make unauthenticated API request
   */
  protected async requestWithoutAuth<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    const url = this.urlBuilder.build(endpoint)

    const config: RequestInit = {
      headers: {
        ...API_CONFIG.DEFAULT_HEADERS,
        ...options.headers,
      },
      ...options,
    }

    const method = (options.method ?? 'GET').toUpperCase()

    let response: Response
    try {
      response = await fetch(url, config)
    } catch (err) {
      const networkError = buildNetworkError(err)
      reportApiFailure(networkError, endpoint, method)
      throw networkError
    }

    if (!response.ok) {
      let errorData: Record<string, unknown> = {}
      const contentType = response.headers.get('content-type')
      if (contentType && contentType.includes('application/json')) {
        try {
          errorData = (await response.json()) as Record<string, unknown>
        } catch {
          const detail = await response.text().catch(() => '')
          errorData = { detail: detail || `HTTP error! status: ${response.status}` }
        }
      } else {
        const detail = await response.text().catch(() => '')
        errorData = { detail: detail || `HTTP error! status: ${response.status}` }
      }

      const apiError = new ApiError(
        extractErrorMessage(errorData, response.status),
        response.status,
        response.statusText,
        errorData,
        extractErrorCode(errorData),
      )
      reportApiFailure(apiError, endpoint, method)
      throw apiError
    }

    return this.parseOkResponseBody<T>(response)
  }

  /**
   * Handle token refresh
   */
  protected async refreshToken(): Promise<{ access: string; refresh: string }> {
    const refreshToken = this.getRefreshToken()
    if (!refreshToken || refreshToken.trim() === '') {
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      window.location.hash = '#/login'
      throw new ApiError('No refresh token available', 401, 'Unauthorized')
    }

    try {
      const response = await this.requestWithoutAuth<{ access: string; refresh: string }>(
        API_CONFIG.ENDPOINTS.TOKEN.REFRESH,
        {
          method: API_CONFIG.METHODS.POST,
          body: JSON.stringify({ refresh: refreshToken }),
        }
      )

      // Store new tokens
      localStorage.setItem('access_token', response.access)
      localStorage.setItem('refresh_token', response.refresh)

      return response
    } catch (error) {
      // Clear tokens on refresh failure
      localStorage.removeItem('access_token')
      localStorage.removeItem('refresh_token')
      throw error
    }
  }

  /**
   * Make request with automatic token refresh
   */
  protected async requestWithRefresh<T>(
    endpoint: string, 
    options: RequestInit = {}
  ): Promise<T> {
    try {
      return await this.request<T>(endpoint, options)
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        // Token expired, try to refresh
        try {
          await this.refreshToken()
          return await this.request<T>(endpoint, options)
        } catch (refreshError) {
          // Refresh failed, redirect to login
          window.location.hash = '#/login'
          throw refreshError
        }
      }
      throw error
    }
  }

  /**
   * Make request for binary data (blob) with automatic token refresh
   */
  protected async requestBlobWithRefresh(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<Blob> {
    const url = this.urlBuilder.build(endpoint)
    
    const makeRequest = async (): Promise<Blob> => {
      const token = this.getAuthToken()
      
      const config: RequestInit = {
        headers: {
          ...(token && { Authorization: `Bearer ${token}` }),
          ...options.headers,
        },
        ...options,
      }

      let response: Response
      try {
        response = await fetch(url, config)
      } catch (err) {
        throw buildNetworkError(err)
      }

      if (!response.ok) {
        if (response.status === 401) {
          throw new ApiError('Unauthorized', 401, 'Unauthorized')
        }
        const errorData = await response.json().catch(() => ({}))
        throw new ApiError(
          extractErrorMessage(errorData, response.status),
          response.status,
          response.statusText,
          errorData
        )
      }

      return await response.blob()
    }

    try {
      return await makeRequest()
    } catch (error) {
      if (error instanceof ApiError && error.status === 401) {
        try {
          await this.refreshToken()
          return await makeRequest()
        } catch (refreshError) {
          window.location.hash = '#/login'
          throw refreshError
        }
      }
      throw error
    }
  }
}

// Create a default instance for convenience
export const baseService = new (class extends BaseService {
  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    const data = await this.requestWithRefresh<T>(endpoint, { method: 'GET' })
    return { data, status: 200, statusText: 'OK' }
  }

  async post<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const responseData = await this.requestWithRefresh<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: responseData, status: 201, statusText: 'Created' }
  }

  async put<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const responseData = await this.requestWithRefresh<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: responseData, status: 200, statusText: 'OK' }
  }

  async patch<T>(endpoint: string, data?: any): Promise<ApiResponse<T>> {
    const responseData = await this.requestWithRefresh<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    })
    return { data: responseData, status: 200, statusText: 'OK' }
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    const responseData = await this.requestWithRefresh<T>(endpoint, {
      method: 'DELETE',
    })
    return { data: responseData, status: 204, statusText: 'No Content' }
  }
})()


